<template>
	<portal
		to="toast"
		:disabled="!siteLevel"
		:slim="slim ? true : undefined"
		ref="portal"
	>
		<transition name="slide-fade">
			<li class="toasts__item" v-if="currentVisible">
				<div :class="['toast', ...stateClassList]" data-testid="toast">
					<div class="toast__indicator-icon" data-testid="toast-icon">
						<i class="icon" :class="iconClassName"></i>
					</div>
					<div class="toast__content-region">
						<header
							class="toast__header"
							data-testid="toast-header"
							v-if="showHeader"
						>
							<div
								class="toast__header-title"
								v-if="title"
								data-testid="title"
							>
								{{ title }}
							</div>
							<div
								class="toast__header-subtitle"
								v-if="subtitle"
								data-testid="subtitle"
							>
								{{ subtitle }}
							</div>
						</header>

						<div class="toast__body" data-testid="bodyMsg">
							<div class="toast__body-msg" v-if="$slots.bodyMsg">
								<slot name="bodyMsg" />
							</div>
							<div
								class="toast__body-action"
								v-if="$slots.bodyAction"
								data-testid="bodyAction"
							>
								<slot name="bodyAction" />
							</div>
						</div>

						<footer
							class="toast__footer-action"
							v-if="$slots.footerAction"
							data-testid="footerAction"
						>
							<slot name="footerAction" />
						</footer>
					</div>

					<ButtonIcon
						class="toast__close-btn"
						iconClassName="icon-remove"
						variant="topRight"
						@click="onClickClose"
						data-testid="toast-close-btn"
					/>
				</div>
			</li>
		</transition>
	</portal>
</template>

<script>
import baseComponentMixin from '@/mixins/base-component-mixin';
import componentStateMixin from '@/mixins/component-state-mixin';
import ButtonIcon from '@/components/forms/button/button-icon.vue';

export default {
	name: 'Toast',
	components: {
		ButtonIcon
	},
	mixins: [baseComponentMixin, componentStateMixin],
	data() {
		return {
			currentVisible: this.visible
		};
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		iconClassName: {
			type: String,
			default: 'notification'
		},
		title: {
			type: String,
			default: null
		},
		subtitle: {
			type: String,
			default: null
		},
		dismissibleTime: {
			type: Number,
			default: null
		},
		/***
		 * If true, the content will be displayed in the app.vue toast portal
		 */
		siteLevel: {
			type: Boolean,
			default: false
		},
		/**
		 * Set to true so portal won't render a wrapper div
		 */
		slim: {
			type: Boolean,
			default: false
		}
	},
	mounted() {
		if (this.visible) {
			this.setDismiss();
		}
	},
	watch: {
		visible(newValue) {
			this.currentVisible = newValue;
			this.setDismiss();
		}
	},
	methods: {
		setDismiss() {
			if (this.dismissibleTime) {
				setTimeout(() => {
					this.dismiss();
				}, this.dismissibleTime * 1000);
			}
		},
		onClickClose() {
			this.currentVisible = false;
			this.$emit('close');
		},
		dismiss() {
			this.currentVisible = false;
			this.$emit('removed');
		}
	},
	computed: {
		showHeader() {
			return this.title || this.subtitle;
		}
	}
};
</script>

<style lang="scss" scoped>
.toast {
	display: grid;
	grid-template-columns: auto 1fr;
	gap: $gutter-lg;
	padding: $padding-md;
	min-width: 200px;
	max-width: 350px;
	align-items: center;
	justify-content: space-between;
	border: $toast-border-width $toast-border-style $neutral-3;
	border-radius: $border-radius;
	background: $neutral-1;
	color: $neutral-5;
	position: relative;

	&__indicator-icon {
		color: $neutral-4;
		font-size: 24px;
		height: 100%;
	}

	&__header {
		margin-bottom: $gutter-md;

		&-title {
			@include heading(md);
		}

		&-subtitle {
			@include heading(sm);
		}
	}

	&__body {
		&-msg {
			font-size: $txt-font-size;
		}

		&-action {
			margin-top: $gutter-md;
		}
	}

	&__footer-action {
		margin-top: $gutter-lg;
	}

	&__close-btn {
		position: absolute;
		cursor: pointer;
		top: $gutter-sm;
		right: $gutter-sm;
		font-size: 12px;
		color: $ui-fg-dark;
	}
}

/*------------------------------------*\
  #STATE-TYPE-STYLES
  This section is only for Toast CSS change via state type.
  Default CSS are located in the base component styles section.
  These classes are added/removed as needed via dev logic
\*------------------------------------*/

.toast {
	&.is-info {
		border-color: var(--ui-neutral);
		background: var(--ui-neutral-light);
		.toast {
			&__indicator-icon {
				color: var(--ui-neutral);
			}
		}
	}

	&.is-positive {
		border-color: var(--ui-positive);
		background: var(--ui-positive-light);
		.toast {
			&__indicator-icon {
				color: var(--ui-positive);
			}
		}
	}

	&.is-negative {
		border-color: var(--ui-negative);
		background: var(--ui-negative-light);
		.toast {
			&__indicator-icon {
				color: var(--ui-negative);
			}
		}
	}

	&.is-warning {
		border-color: var(--ui-warning);
		background: var(--ui-warning-light);
		.toast {
			&__indicator-icon {
				color: var(--ui-warning);
			}
		}
	}

	&.is-mail {
		border-color: var(--ui-indigo);
		background: var(--ui-indigo-light);
		.toast {
			&__indicator-icon {
				color: var(--ui-indigo);
			}
		}
	}

	&.is-time {
		border-color: var(--ui-purple);
		background: var(--ui-purple-light);
		.toast {
			&__indicator-icon {
				color: var(--ui-purple);
			}
		}
	}
}
</style>
